import { isMobileOnly } from 'react-device-detect';
import { T2C_NUMBER_ASSOCIATED_ANOTHER_ACCOUNT_MSG } from '../constants/response-error-messages';
import { NON_DIGITS_REGEX } from '../constants/regex';
import { RECAPTCHA_TEST_SITE_KEY, Environment } from '../constants/general';
import { addGiftCardSiteKey } from '../constants/gift-card';
import {
  IB2BPaymentCardsResponse,
  T2CMultipleApproverResponse,
} from '../models/b2b/payments';

export const isSsr = typeof window === 'undefined';

export const getHost = (): string => {
  if (isSsr) return '';

  if (process.env.NODE_ENV === 'production' && !process.env.DOMAIN_NAME) {
    return `https://${window.location.hostname}`;
  }

  return window.location.origin;
};

export const getChannelID = (): number => (isMobileOnly ? 2 : 1);

export const generateXB3TraceID = () =>
  Math.random().toString(16).substring(2) +
  new Date().getTime().toString(16).substring(0, 15);

export const removeNonDigitsFromString = (string = ''): string =>
  string.replace(NON_DIGITS_REGEX, '');

export const getTmxId = (): string => {
  if (isSsr) return '';
  let nativeTMXID: string | null = '';
  if (window.__EXPERIENCE_CONTEXT__?.isConsumerApp) {
    nativeTMXID = getUrlParam('guid');
  }
  return nativeTMXID || window.THD_ACCOUNT_LIB?.TMX?.getTMXsessionId();
};

export const getUrlParam = (key: string) => {
  const searchParams =
    typeof window !== 'undefined' ? window.location?.search : '';
  const urlParams = new URLSearchParams(searchParams);
  return urlParams.get(key);
};

export const scrollToTop = () => {
  if (isSsr) return;
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  });
};

export const getHyphenatedGiftCard = (card: string): string => {
  const cardNumber = card.replace(/\D/g, '');
  const cardLength = cardNumber.length;

  if (cardLength > 4 && cardLength <= 12) {
    return `${cardNumber.substring(0, 4)}-${cardNumber.substring(4)}`;
  }

  if (cardLength > 12 && cardLength <= 16) {
    return `${cardNumber.substring(0, 4)}-${cardNumber.substring(
      4,
      12,
    )}-${cardNumber.substring(12)}`;
  }

  if (cardLength > 16) {
    return `${cardNumber.substring(0, 4)}-${cardNumber.substring(
      4,
      12,
    )}-${cardNumber.substring(12, 16)}-${cardNumber.substring(16)}`;
  }

  return cardNumber;
};

export const getHyphenatedPhoneNumber = (phone: string): string => {
  const phoneNumber = phone.replace(/\D/g, '');
  const phoneLength = phoneNumber.length;

  if (phoneLength > 3 && phoneLength <= 6) {
    return `${phoneNumber.substring(0, 3)}-${phoneNumber.substring(3)}`;
  }

  if (phoneLength > 6) {
    return `${phoneNumber.substring(0, 3)}-${phoneNumber.substring(
      3,
      6,
    )}-${phoneNumber.substring(6, 10)}`;
  }

  return phoneNumber;
};

export const getEnvironment = () => {
  if (isSsr) return '';

  if (window.location.hostname.endsWith('homedepot.com')) {
    return Environment.PROD;
  }
  if (window.location.hostname.endsWith('hd-qa74.homedepotdev.com')) {
    return Environment.STAGE;
  }
  if (window.location.hostname.endsWith('hd-qa71.homedepotdev.com')) {
    return Environment.DEV;
  }
  return '';
};

export const getSiteKey = (isB2B: boolean) => {
  if (isSsr) return '';
  const svocID = window.THDCustomer.default.svocID;
  const userID = window.THDCustomer.default.userID;

  const allowedQEAccounts =
    window.__EXPERIENCE_PROPS__?.expConfiguration?.ALLOWED_QE_ACCOUNTS || '';

  if (svocID && userID && allowedQEAccounts) {
    const allowedPairs = allowedQEAccounts.split(',');
    const currentUserPair = `${svocID}+${userID}`;

    if (allowedPairs.includes(currentUserPair)) {
      return RECAPTCHA_TEST_SITE_KEY;
    }
  }

  if (window.location.hostname.endsWith('homedepot.com')) {
    return isB2B
      ? '6LfEHBkTAAAAAHX6YgeUw9x1Sutr7EzhMdpbIfWJ'
      : '6LcAvCkaAAAAANWymwFQVvpFDQIBDCI4NbAvXGJL';
  }
  return '6Le3GRkTAAAAAPpXON0jcJCLrYZnm-ZqyLhbCLbX';
};

export const getPaymentModalsVersion = () =>
  typeof window !== 'undefined'
    ? window.__EXPERIENCE_PROPS__?.expConfiguration?.paymentModalsVersion
    : '';

export const getConfigurationValue = (value: string) =>
  typeof window !== 'undefined'
    ? window.__EXPERIENCE_PROPS__?.expConfiguration[value]
    : '';

export const getReCaptchaKey = () => {
  if (isSsr) {
    return '';
  }
  if (window.location.hostname.endsWith('homedepot.com')) {
    return '6LcGXvclAAAAAM7ZE9BVUDtZ_x5jDvPB4Ml1fYwK';
  }
  if (window.location.hostname.includes('qa74')) {
    return '6LeNZeAlAAAAAGEsAyo1uZoTvfgSdLUdlfyWpIYp';
  }
  if (window.location.hostname.includes('qa71')) {
    return '6LcjiJMlAAAAAK7W4IzEyg1u9Ls7hML6cSgJOh1U';
  }

  return '';
};

export const getAddGiftCardSiteKey = () => {
  const env = getEnvironment();
  if (env) return addGiftCardSiteKey[env];
  return '';
};

export const triggerLifeCycleEventBus = (
  id: string,
  type: string,
  payload?: object,
) => {
  if (isSsr) {
    return;
  }
  window.LIFE_CYCLE_EVENT_BUS?.lifeCycle?.trigger(id, {
    type,
    payload,
  });
};

export const sendPostMessage = <T>(
  message: T,
  targetOrigin = window.origin,
  transfer?: Transferable[],
) => {
  if (isSsr) {
    return;
  }
  window.postMessage(message, targetOrigin, transfer);
};

export const timeout = (ms: number) => {
  return new Promise((__, reject) =>
    setTimeout(() => reject(new Error('Timeout after ' + ms + ' ms')), ms),
  );
};

export const getMAApproverList = (
  ccApprovers: IB2BPaymentCardsResponse['paymentCards']['t2cApprovers'],
  phaApprovers: IB2BPaymentCardsResponse['paymentCards']['t2cApprovers'],
): T2CMultipleApproverResponse => {
  if (!ccApprovers?.length) {
    return phaApprovers || [];
  }
  if (!phaApprovers?.length) {
    return ccApprovers;
  }
  return ccApprovers.length >= phaApprovers.length ? ccApprovers : phaApprovers;
};

export const formatRedactedPhoneNumbers = (phoneNumbers: string[]): string => {
  if (!phoneNumbers?.length) return '';

  if (phoneNumbers.length === 1) {
    return phoneNumbers[0];
  }

  return phoneNumbers.join(', ');
};

export const extractPhoneNumbersFromErrorMessage = (
  errorMessage: string,
): string[] => {
  try {
    const match = errorMessage.match(/\[(.*?)\]/);
    if (!match || !match[1]) return [];

    return match[1].split(',').map((number) => number.trim());
  } catch {
    return [];
  }
};

export const getDuplicateT2cErrorMessage = (
  phoneNumbers: string[],
  phoneNumbersText: string,
) => {
  if (!phoneNumbers?.length) {
    return T2C_NUMBER_ASSOCIATED_ANOTHER_ACCOUNT_MSG.message;
  }

  return phoneNumbers.length > 1
    ? `${phoneNumbersText} are in use on another account.`
    : `${phoneNumbersText} is in use on another account.`;
};

export const getDuplicateT2cErrorMessages = (
  phoneNumbers: string[],
): string[] => {
  if (!phoneNumbers?.length) {
    return [T2C_NUMBER_ASSOCIATED_ANOTHER_ACCOUNT_MSG.message];
  }

  return phoneNumbers.map((number) => {
    const masked = number.length > 4 ? '******' + number.slice(-4) : number;
    return `Mobile number ${masked} is already assigned to another account.`;
  });
};
